import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import Title from '@/components/ui/Title';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import SurveyForm from './SurveyForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  createSurveyAnswerOption,
  createSurveyQuestion,
  createSurveyTemplate,
  deleteSurveyAnswerOption,
  deleteSurveyTemplate,
  fetchSurveyTemplate,
  listSurveyTemplates,
  updateSurveyAnswerOption,
  updateSurveyQuestion,
  updateSurveyTemplate,
} from '@/features/webinar/webinarThunks';
import type {
  SurveyAnswerOption,
  SurveyAnswerOptionInput,
  SurveyQuestionInput,
  SurveyTemplate,
  SurveyTemplateInput,
} from '@/features/webinar/types';
import type { RootState } from '@/app/store';
import { useTranslation } from 'react-i18next';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Skeleton } from '@/components/ui/skeleton';
import EmptyInputFile from '../EmptyInputFile';
import { toast } from 'react-toastify';
import { mapErrorMessage } from '@/lib/mapApiError';
import type { AppError } from '@/features/auth/types';
import TimeCodeComponent, { type TimeCodeScheduling } from '../../evergreen/TimeCodeComponent';
import { getEvergreenTimelineResourceIds } from '@/features/webinar/evergreenTimelineUtils';
import { mergeInteractionFormStates } from '@/lib/interactionFormStates';
import { cn } from '@/lib/utils';
import type {
  InteractionGroupHandle,
  InteractionItemHandle,
  SaveAllResult,
} from '../interactionBatch';

type SurveyItem = {
  id: string;
  existingSurvey?: SurveyTemplate;
};

const SURVEY_ACCORDION_VALUE = 'accordion-1';

interface SurveysFormProps {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  openAccordionTrigger?: boolean;
  onAccordionOpened?: () => void;
  isEvergreen?: boolean;
  scheduling?: TimeCodeScheduling;
  videoUrl?: string;
  videoFallbackUrl?: string;
  readOnly?: boolean;
  // Mode sauvegarde globale : pas de bouton par sondage, le parent pilote saveAll.
  batchMode?: boolean;
  // Remonte l'état « modifications non enregistrées » du groupe au parent.
  onDirtyChange?: (dirty: boolean) => void;
}

const SurveysForm = forwardRef<InteractionGroupHandle, SurveysFormProps>(function SurveysForm(
  {
    webinarId,
    workspaceId,
    isEdit,
    openAccordionTrigger,
    onAccordionOpened,
    isEvergreen,
    scheduling,
    videoUrl,
    videoFallbackUrl,
    readOnly,
    batchMode = false,
    onDirtyChange,
  },
  ref,
) {
  const locked = readOnly === true;
  const dispatch = useAppDispatch();
  const { t } = useTranslation('createWebinar');
  const surveyTemplates = useAppSelector((state: RootState) => state.webinar.surveyTemplates);
  const surveyTemplatesLoading = useAppSelector(
    (state: RootState) => state.webinar.surveyTemplatesLoading,
  );
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const evergreenSurveyIds = useMemo(
    () =>
      isEvergreen && !scheduling
        ? getEvergreenTimelineResourceIds(evergreenTimeline, 'SURVEY')
        : null,
    [isEvergreen, scheduling, evergreenTimeline],
  );

  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );
  const pendingOperationsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    dispatch(listSurveyTemplates({ workspaceId, webinarId }));
  }, [workspaceId, webinarId]);

  const [surveys, setSurveys] = useState<SurveyItem[]>([]);
  const [accordionValue, setAccordionValue] = useState<string>('');
  const [submittingSurveys, setSubmittingSurveys] = useState<Record<string, boolean>>({});

  const [innerAccordionValue, setInnerAccordionValue] = useState<string>('');

  // Handles impératifs des formulaires enfants, indexés par id de sondage.
  const itemRefs = useRef<Map<string, InteractionItemHandle | null>>(new Map());
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

  const handleChildDirty = useCallback((surveyItemId: string, dirty: boolean) => {
    setDirtyMap((prev) =>
      prev[surveyItemId] === dirty ? prev : { ...prev, [surveyItemId]: dirty },
    );
  }, []);

  // On ne tient compte que des éléments présents (les ids supprimés/remplacés sont ignorés).
  const hasDirty = surveys.some((survey) => dirtyMap[survey.id]);

  useEffect(() => {
    onDirtyChange?.(hasDirty);
  }, [hasDirty, onDirtyChange]);

  useEffect(() => {
    if (surveyTemplatesLoading) return;

    let templatesToDisplay = surveyTemplates.filter(
      (template) => template.waitingRoomSurvey !== true,
    );
    if (evergreenSurveyIds) {
      templatesToDisplay = templatesToDisplay.filter((t) => evergreenSurveyIds.has(t.id));
    }

    const savingIds = new Set(
      Object.entries(submittingSurveys)
        .filter(([, isSubmitting]) => isSubmitting)
        .map(([id]) => id),
    );

    setSurveys((prevSurveys) =>
      mergeInteractionFormStates(prevSurveys, templatesToDisplay, {
        toState: (template) => ({ id: String(template.id), existingSurvey: template }),
        isDraft: (survey) => survey.existingSurvey == null,
        savingIds,
      }),
    );
  }, [surveyTemplatesLoading, surveyTemplates, evergreenSurveyIds, submittingSurveys]);

  useEffect(() => {
    if (openAccordionTrigger) {
      setAccordionValue(SURVEY_ACCORDION_VALUE);
      onAccordionOpened?.();
    }
  }, [openAccordionTrigger, onAccordionOpened]);

  const addSurvey = () => {
    if (locked) return;
    if (surveys.length >= 10) return;
    const newSurvey = { id: crypto.randomUUID() };
    setSurveys((prev) => [...prev, newSurvey]);
    const newIndex = surveys.length;
    setInnerAccordionValue(`item-survey-${newIndex}`);
  };

  const handleInnerAccordionChange = (value: string | undefined) => {
    const accordionValue = value || '';
    setInnerAccordionValue(accordionValue);
  };

  const removeSurvey = async (surveyId: string) => {
    if (locked) return;
    const surveyEntry = surveys.find((survey) => survey.id === surveyId);
    if (surveys.length > 1) {
      setSurveys((prev) => prev.filter((survey) => survey.id !== surveyId));
    } else {
      setSurveys([{ id: crypto.randomUUID() }]);
    }
    // Suppression back-end uniquement si le sondage existe déjà en base
    // (un sondage non encore enregistré n'a pas de template à supprimer).
    const templateId = surveyEntry?.existingSurvey?.id;
    if (templateId != null) {
      await dispatch(deleteSurveyTemplate({ workspaceId, webinarId, templateId }));
    }
  };

  const removeSurveyAnswerOption = (surveyId: string, answerOptionId: number) => {
    const surveyEntry = surveys.find((survey) => survey.id === surveyId);
    const existingSurvey = surveyEntry?.existingSurvey;
    if (existingSurvey && answerOptionId != null) {
      dispatch(
        deleteSurveyAnswerOption({
          workspaceId,
          webinarId,
          templateId: existingSurvey.id,
          questionId: existingSurvey.questions[0].id,
          optionId: answerOptionId,
        }),
      );
    }
  };

  const getColorValueById = (
    colorId: string | undefined,
    customColor: string | undefined,
    defaultColor: string,
    visualIdentity: WorkspaceVisualIdentity | null | undefined,
  ): string => {
    if (!colorId || colorId === 'custom') {
      return customColor || defaultColor;
    }

    if (/^#[0-9A-Fa-f]{6}$/.test(colorId)) {
      return colorId;
    }

    if (visualIdentity) {
      if (colorId === 'accent') {
        return visualIdentity.accentColor || defaultColor;
      }
      if (colorId === 'background') {
        return visualIdentity.backgroundColor || defaultColor;
      }
      if (colorId === 'contrast') {
        return visualIdentity.contrastColor || defaultColor;
      }
      if (colorId === 'secondary-background') {
        return visualIdentity.secondaryBackgroundColor || defaultColor;
      }
      if (colorId === 'gradient1-color1') {
        return 'gradient1';
      }
      if (colorId === 'gradient2-color1') {
        return 'gradient2';
      }
    }

    if (colorId === 'black') {
      return '#000000';
    }
    if (colorId === 'white') {
      return '#FFFFFF';
    }

    return defaultColor;
  };

  const resolveColorValue = (
    value?: string,
    custom?: string | null,
    visualIdentity?: WorkspaceVisualIdentity | null,
  ) => {
    if (!value) return null;
    if (value === 'custom') {
      return custom ?? null;
    }
    if (value === 'no-border') {
      return null;
    }

    return getColorValueById(value, custom ?? undefined, '#000000', visualIdentity);
  };

  const handleSurveySubmit = async (
    surveyData: any,
    surveyId: string,
    options?: { silent?: boolean },
  ) => {
    if (locked) return;
    if (pendingOperationsRef.current.has(surveyId)) {
      console.log('Survey submission already in progress for', surveyId);
      return;
    }

    try {
      setSubmittingSurveys((prev) => ({ ...prev, [surveyId]: true }));
      pendingOperationsRef.current.add(surveyId);

      const surveyEntry = surveys.find((survey) => survey.id === surveyId);
      const existingSurvey = surveyEntry?.existingSurvey;
      const templateId = existingSurvey?.id;

      const backgroundColor = resolveColorValue(
        surveyData.backgroundColor,
        surveyData.customBackgroundColor,
        visualIdentity,
      );
      const borderColor = resolveColorValue(
        surveyData.borderColor,
        surveyData.customBorderColor,
        visualIdentity,
      );
      const accentColor = resolveColorValue(
        surveyData.accentColor,
        surveyData.customAccentColor,
        visualIdentity,
      );
      const titleColor = surveyData.titleColor || '#000000';

      const surveyTemplateInput: SurveyTemplateInput = {
        title: surveyData.title.trim(),
        backgroundColor: backgroundColor || null,
        borderColor: borderColor,
        accentColor: accentColor || null,
        titleColor: titleColor,
        waitingRoomSurvey: false,
      };

      let finalTemplateId: number;
      let finalQuestionId: number;

      if (!templateId) {
        finalTemplateId = await dispatch(
          createSurveyTemplate({
            workspaceId,
            webinarId,
            input: surveyTemplateInput,
          }),
        ).unwrap();

        if (!finalTemplateId) {
          console.error('Failed to create survey template');
          return;
        }

        const questionInput: SurveyQuestionInput = {
          textContent: surveyData.question.textContent.trim(),
          type: surveyData.question.type,
          position: surveyData.question.position ?? 0,
          required: surveyData.question.required ?? false,
          maxSelections: surveyData.question.maxSelections ?? null,
          randomizeOptions: surveyData.question.randomizeOptions ?? false,
        };

        finalQuestionId = await dispatch(
          createSurveyQuestion({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
            input: questionInput,
          }),
        ).unwrap();

        if (!finalQuestionId) {
          console.error('Failed to create survey question');
          return;
        }

        const existingOptionIds = new Set<number>();
        const createdOptionTexts = new Set<string>();

        for (const option of surveyData.question.options || []) {
          if (!option.textContent?.trim()) {
            continue;
          }

          if (option.id && existingOptionIds.has(option.id)) {
            continue;
          }

          const normalizedText = option.textContent.trim().toLowerCase();
          if (createdOptionTexts.has(normalizedText)) {
            continue;
          }

          if (!option.id) {
            const optionInput: SurveyAnswerOptionInput = {
              textContent: option.textContent.trim(),
              position: option.position ?? existingOptionIds.size,
            };

            const optionId = await dispatch(
              createSurveyAnswerOption({
                workspaceId,
                webinarId,
                templateId: finalTemplateId,
                questionId: finalQuestionId,
                input: optionInput,
              }),
            ).unwrap();

            if (optionId) {
              existingOptionIds.add(optionId);
              createdOptionTexts.add(normalizedText);
            }
          } else {
            existingOptionIds.add(option.id);
            createdOptionTexts.add(normalizedText);
          }
        }

        const createdSurvey = await dispatch(
          fetchSurveyTemplate({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
          }),
        ).unwrap();

        setSurveys((prevSurveys) =>
          prevSurveys.map((survey) =>
            survey.id === surveyId
              ? {
                  id: String(finalTemplateId),
                  existingSurvey: createdSurvey,
                }
              : survey,
          ),
        );

        if (!options?.silent) {
          toast.success(t('surveys.surveyCreatedSuccess'));
        }
        setInnerAccordionValue('');
      } else {
        finalTemplateId = templateId;

        const currentSurvey = await dispatch(
          fetchSurveyTemplate({
            workspaceId,
            webinarId,
            templateId,
          }),
        ).unwrap();

        await dispatch(
          updateSurveyTemplate({
            workspaceId,
            webinarId,
            templateId,
            input: surveyTemplateInput,
          }),
        ).unwrap();

        const existingQuestion = currentSurvey?.questions?.[0] || existingSurvey?.questions?.[0];
        const questionInput: SurveyQuestionInput = {
          textContent: surveyData.question.textContent.trim(),
          type: surveyData.question.type,
          position: surveyData.question.position ?? 0,
          required: surveyData.question.required ?? false,
          maxSelections: surveyData.question.maxSelections ?? null,
          randomizeOptions: surveyData.question.randomizeOptions ?? false,
        };

        if (existingQuestion?.id) {
          finalQuestionId = existingQuestion.id;
          await dispatch(
            updateSurveyQuestion({
              workspaceId,
              webinarId,
              templateId,
              questionId: existingQuestion.id,
              input: questionInput,
            }),
          ).unwrap();
        } else {
          finalQuestionId = await dispatch(
            createSurveyQuestion({
              workspaceId,
              webinarId,
              templateId,
              input: questionInput,
            }),
          ).unwrap();

          if (!finalQuestionId) {
            console.error('Failed to create survey question');
            return;
          }
        }

        const existingServerOptions = currentSurvey?.questions?.[0]?.options || [];
        const existingOptionMap = new Map<number, SurveyAnswerOption>(
          existingServerOptions.map((opt) => [opt.id, opt]),
        );
        const existingOptionTextMap = new Map<string, SurveyAnswerOption>(
          existingServerOptions.map((opt) => [opt.textContent.trim().toLowerCase(), opt]),
        );

        const processedOptionIds = new Set<number>();
        const processedOptionTexts = new Set<string>();

        for (const option of surveyData.question.options || []) {
          if (!option.textContent?.trim()) {
            continue;
          }

          const normalizedText = option.textContent.trim().toLowerCase();

          if (option.id && processedOptionIds.has(option.id)) {
            processedOptionTexts.add(normalizedText);
            continue;
          }

          if (processedOptionTexts.has(normalizedText)) {
            continue;
          }

          const trimmedTextContent = option.textContent.trim();
          const calculatedPosition = option.position ?? processedOptionIds.size;

          if (option.id && existingOptionMap.has(option.id)) {
            const serverOption = existingOptionMap.get(option.id)!;
            const textContentChanged = serverOption.textContent.trim() !== trimmedTextContent;
            const positionChanged = serverOption.position !== calculatedPosition;

            if (textContentChanged || positionChanged) {
              const optionInput: SurveyAnswerOptionInput = {
                textContent: trimmedTextContent,
                position: calculatedPosition,
              };

              await dispatch(
                updateSurveyAnswerOption({
                  workspaceId,
                  webinarId,
                  templateId,
                  questionId: finalQuestionId,
                  optionId: option.id,
                  input: optionInput,
                }),
              ).unwrap();
            }
            processedOptionIds.add(option.id);
            processedOptionTexts.add(normalizedText);
          } else if (!option.id) {
            const existingByText = existingOptionTextMap.get(normalizedText);

            if (existingByText) {
              processedOptionIds.add(existingByText.id);
              processedOptionTexts.add(normalizedText);
            } else {
              const optionInput: SurveyAnswerOptionInput = {
                textContent: trimmedTextContent,
                position: calculatedPosition,
              };

              const optionId = await dispatch(
                createSurveyAnswerOption({
                  workspaceId,
                  webinarId,
                  templateId,
                  questionId: finalQuestionId,
                  input: optionInput,
                }),
              ).unwrap();

              if (optionId) {
                processedOptionIds.add(optionId);
                processedOptionTexts.add(normalizedText);
              }
            }
          }
        }

        const updatedSurvey = await dispatch(
          fetchSurveyTemplate({
            workspaceId,
            webinarId,
            templateId,
          }),
        ).unwrap();

        setSurveys((prevSurveys) =>
          prevSurveys.map((survey) =>
            survey.id === surveyId
              ? {
                  ...survey,
                  existingSurvey: updatedSurvey,
                }
              : survey,
          ),
        );

        if (!options?.silent) {
          toast.success(t('surveys.surveyUpdatedSuccess'));
        }

        setInnerAccordionValue('');
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      if (options?.silent) {
        // En mode lot, on laisse l'orchestrateur parent gérer l'erreur (un seul toast).
        throw error;
      }
      const err = error as AppError | { message?: string; code?: string; status?: number };
      const errorMessage = mapErrorMessage(err?.message, err?.code, err?.status);
      toast.error(t(errorMessage) || t('errors.generic') || 'Une erreur est survenue');
    } finally {
      setSubmittingSurveys((prev) => ({ ...prev, [surveyId]: false }));
      pendingOperationsRef.current.delete(surveyId);
    }
  };

  // Sauvegarde de lot : valide tous les sondages modifiés, puis les persiste en séquence.
  const saveAll = async (): Promise<SaveAllResult> => {
    if (locked) return { ok: true, saved: 0 };

    // 1) Validation + capture des valeurs des sondages modifiés.
    // On capture AVANT de persister : créer un sondage met à jour le store, ce qui
    // reconstruit la liste locale et démonte les autres formulaires (donc leurs refs).
    const payloads: { id: string; values: unknown }[] = [];
    for (let index = 0; index < surveys.length; index++) {
      const survey = surveys[index];
      const handle = itemRefs.current.get(survey.id);
      if (!handle?.isDirty()) continue;
      const valid = await handle.validate();
      const values = handle.getValues() as { title?: string };
      if (!valid) {
        setAccordionValue(SURVEY_ACCORDION_VALUE);
        setInnerAccordionValue(`item-survey-${index}`);
        return { ok: false, reason: 'validation', label: values.title?.trim() || undefined };
      }
      payloads.push({ id: survey.id, values });
    }

    if (payloads.length === 0) {
      return { ok: true, saved: 0 };
    }

    // 2) Persistance séquentielle à partir des valeurs capturées (chaque sondage reste
    // une cascade non atomique). On ne dépend plus des refs, qui peuvent être démontées.
    let failing: { id: string; values: unknown } | undefined;
    try {
      for (const payload of payloads) {
        failing = payload;
        await handleSurveySubmit(payload.values, payload.id, { silent: true });
        // En édition l'id ne change pas (pas de remount) : on remet le form à « non modifié ».
        itemRefs.current.get(payload.id)?.markPristine();
      }
    } catch (error) {
      console.error('Batch survey save failed:', error);
      const label = (failing?.values as { title?: string } | undefined)?.title?.trim() || undefined;
      return { ok: false, reason: 'error', label };
    }

    return { ok: true, saved: payloads.length };
  };

  useImperativeHandle(
    ref,
    () => ({
      hasDirty: () => hasDirty,
      saveAll,
    }),
    // saveAll est recréée à chaque rendu ; on garde le handle aligné sur l'état courant.
    [hasDirty, surveys],
  );

  if (surveyTemplatesLoading) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="item-1">
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger>
              <Title level={2}>{t('surveys.sectionTitle')}</Title>
            </AccordionTrigger>
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <div className="space-y-4">
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="pt-4">
          <Skeleton className="h-10 w-40" />
        </div>
      </div>
    );
  }

  const surveysForAccordionFill = evergreenSurveyIds
    ? surveyTemplates.filter((t) => !t.waitingRoomSurvey && evergreenSurveyIds.has(t.id))
    : surveyTemplates;

  return (
    <div>
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem
          value="accordion-1"
          className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
        >
          <AccordionTrigger
            fillForm={surveysForAccordionFill.length > 0}
            className=" rounded-[20px]"
          >
            <div className="flex flex-col  gap-2">
              <Title level={2}>{t('surveys.sectionTitle')}</Title>
              <p className="text-sm font-normal text-tertiary">{t('surveys.description')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4 bg-subtitle' : ''}`}
          >
            {surveys.length === 0 ? (
              <div
                onClick={() => {
                  if (!locked && (!isEvergreen || scheduling)) addSurvey();
                }}
                className={cn(locked && 'cursor-default opacity-90')}
              >
                <EmptyInputFile contentType={t('surveys.emptyContentType')} type="survey" />
              </div>
            ) : (
              <>
                <Accordion
                  type="single"
                  collapsible
                  className="space-y-[10px]"
                  value={innerAccordionValue}
                  onValueChange={handleInnerAccordionChange}
                >
                  {surveys.map((survey, index) => {
                    return (
                      <div key={survey.id}>
                        <SurveyForm
                          ref={(el) => {
                            if (el) itemRefs.current.set(survey.id, el);
                            else itemRefs.current.delete(survey.id);
                          }}
                          surveyIndex={index}
                          // surveyId={survey.id}
                          canRemove={surveys.length > 1 || !!survey?.existingSurvey}
                          onRemove={() => removeSurvey(survey.id)}
                          onRemoveAnswerOption={(answerOptionId) =>
                            removeSurveyAnswerOption(survey.id, answerOptionId)
                          }
                          onSubmit={(data) => handleSurveySubmit(data, survey.id)}
                          existingSurvey={survey.existingSurvey}
                          isSubmitting={submittingSurveys[survey.id] || false}
                          isEvergreen={isEvergreen}
                          readOnly={readOnly}
                          batchMode={batchMode}
                          onDirtyChange={(dirty) => handleChildDirty(survey.id, dirty)}
                        />
                        {videoUrl && (
                          <TimeCodeComponent
                            videoUrl={videoUrl}
                            scheduling={scheduling}
                            videoFallbackUrl={videoFallbackUrl}
                            selectedEventType="SURVEY"
                            selectedResourceId={survey.existingSurvey?.id ?? null}
                            readOnly={locked}
                          />
                        )}
                      </div>
                    );
                  })}
                </Accordion>

                {(!isEvergreen || !!scheduling) && !locked && surveys.length < 10 && (
                  <button
                    type="button"
                    onClick={addSurvey}
                    className="flex items-center gap-2 pt-4"
                  >
                    <Plus className="size-6" />
                    <Label className="cursor-pointer">{t('surveys.addSurveyButton')}</Label>
                  </button>
                )}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
});

export default SurveysForm;
